/**
 * API client.
 *
 * One place that knows how to talk to the server, so error handling and the
 * session token live in a single file rather than being repeated at every call
 * site.
 */

const TOKEN_KEY = 'asp.session';

/**
 * The session token is kept in sessionStorage, not localStorage: it dies with the
 * tab. The server also sets an httpOnly cookie, which is the real credential —
 * this copy exists only so the SPA can send an Authorization header and know
 * whether to show the sign-in screen.
 */
export const tokenStore = {
  get: (): string | null => sessionStorage.getItem(TOKEN_KEY),
  set: (token: string): void => sessionStorage.setItem(TOKEN_KEY, token),
  clear: (): void => sessionStorage.removeItem(TOKEN_KEY),
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields?: Record<string, string>;
  readonly hint?: string;

  constructor(
    status: number,
    code: string,
    message: string,
    fields?: Record<string, string>,
    hint?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.hint = hint;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** For file uploads; sets no Content-Type so the browser adds the boundary. */
  formData?: FormData;
  signal?: AbortSignal;
}

let onUnauthorised: (() => void) | null = null;

/** Lets the auth provider clear its state when any request finds the session gone. */
export function setUnauthorisedHandler(handler: () => void): void {
  onUnauthorised = handler;
}

/**
 * Where the API lives. Empty in development (Vite proxies /api) and in any deployment
 * that serves both from one origin. Set to the API's origin when they are split, as
 * they are with Cloudflare Pages + a Supabase Edge Function.
 *
 * Auth travels as a Bearer token from sessionStorage rather than a cookie, which is
 * what makes a cross-origin split work at all — SameSite=Lax would block the cookie.
 */
const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = tokenStore.get();
  const headers: Record<string, string> = {};

  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const url = `${API_BASE}${path.startsWith('/') ? path : `/api/${path}`}`;

  const response = await fetch(url, {
    method: options.method ?? (options.body || options.formData ? 'POST' : 'GET'),
    headers,
    body: options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
    credentials: 'include',
    signal: options.signal,
  });

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = payload?.error ?? {};

    // A 401 anywhere means the session has gone — expired, revoked, or the account
    // suspended. Clear it once, centrally, rather than in every component.
    if (response.status === 401) {
      tokenStore.clear();
      onUnauthorised?.();
    }

    throw new ApiError(
      response.status,
      error.code ?? 'unknown',
      error.message ?? 'Something went wrong. Please try again.',
      error.fields,
      error.hint,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => apiFetch<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) => apiFetch<T>(path, { method: 'POST', formData }),
};

/**
 * Opens a file the API serves — a PDF, a quotation — in a new tab.
 *
 * A plain `<a href>` cannot do this, for two reasons that bite in different ways. It sends
 * no Authorization header, so the API answers 401; and a relative href resolves against the
 * *site* origin, which is Cloudflare Pages, not the API — so the browser gets the SPA back
 * and shows the sign-in page, which looks like a session problem rather than a broken link.
 *
 * Fetching with the token and opening the blob avoids both, and keeps the token out of the
 * URL where it would end up in history and server logs.
 */
export async function openAuthenticated(path: string, onError?: (message: string) => void) {
  const token = tokenStore.get();
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error(String(response.status));
    const url = URL.createObjectURL(await response.blob());
    window.open(url, '_blank');
    // Revoked late: revoking immediately can beat the new tab to the file.
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch {
    onError?.('That file could not be opened. It may have been removed.');
  }
}

/** Builds a query string, omitting empty values so URLs stay readable. */
export function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}
